import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import MoneyInput from "@/components/MoneyInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCLP } from "@/lib/format";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const keyOf = (a, i) => String(a.id ?? i);
const otherKey = (o, j) => `other:${o.id ?? j}`;

export const IncomeBreakdown = ({ app }) => {
  const items = app.income_review?.items || {};
  const adults = app.income?.adults || [];
  const others = app.income?.others || [];
  const extra = app.income_review?.extra || [];
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm" data-testid="income-breakdown">
        <thead className="text-[11px] uppercase text-slate-500 text-left">
          <tr><th className="py-1 px-1">Ingreso</th><th className="px-1 text-right">Declarado</th><th className="px-1 text-right">Documentos</th><th className="px-1 text-right">Usado en cálculo</th></tr>
        </thead>
        <tbody>
          {adults.map((a, i) => {
            const ov = items[keyOf(a, i)];
            const used = ov ?? a.income_used ?? a.income_declared ?? 0;
            const why = ov != null ? "comisión" : a.income_used != null && a.income_used !== a.income_declared ? "documentos" : "declarado";
            return (
              <tr key={`a${i}`} className="border-t border-border" data-testid={`income-row-${i}`}>
                <td className="py-1.5 px-1">{a.nombre}{a.role === "conyuge" ? " (cónyuge)" : ""}</td>
                <td className="px-1 text-right">{formatCLP(a.income_declared || 0)}</td>
                <td className={`px-1 text-right ${a.verification_status === "mismatch" ? "text-amber-700" : "text-emerald-700"}`}>{a.income_detected != null ? formatCLP(a.income_detected) : "—"}</td>
                <td className="px-1 text-right font-semibold">{formatCLP(used)} <span className="text-[10px] font-normal text-muted-foreground">({why})</span></td>
              </tr>
            );
          })}
          {others.map((o, j) => {
            const ov = items[otherKey(o, j)];
            return (
              <tr key={`o${j}`} className="border-t border-border" data-testid={`income-other-row-${j}`}>
                <td className="py-1.5 px-1">Otro ingreso: {o.tipo || "—"}{o.descripcion ? ` · ${o.descripcion}` : ""}</td>
                <td className="px-1 text-right">{formatCLP(o.amount || 0)}</td>
                <td className="px-1 text-right">—</td>
                <td className="px-1 text-right font-semibold">{formatCLP(ov ?? o.amount ?? 0)} <span className="text-[10px] font-normal text-muted-foreground">({ov != null ? "comisión" : "declarado"})</span></td>
              </tr>
            );
          })}
          {extra.map((x, k) => (
            <tr key={`x${k}`} className="border-t border-border text-primary">
              <td className="py-1.5 px-1">{x.label} (agregado por comisión)</td><td /><td />
              <td className="px-1 text-right font-semibold">{formatCLP(x.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function IncomeAdjust({ appId, app, canAdjust, onSaved }) {
  const review = app.income_review || {};
  const [items, setItems] = useState({});
  const [extra, setExtra] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setItems(review.items || {});
    setExtra(review.extra || []);
    setNote(review.note || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/applications/${appId}/adjust-income`, { items, extra, note: note || null });
      toast.success("Ingresos ajustados y beca recalculada.");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setExtraAt = (i, k, v) => setExtra(extra.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  return (
    <div className="mt-5 border-t border-border pt-4 space-y-3" data-testid="income-adjust">
      <p className="text-xs font-semibold uppercase text-slate-500">Ajuste de ingresos por la comisión</p>
      {(app.income?.adults || []).map((a, i) => {
        const k = keyOf(a, i);
        const base = a.income_used ?? a.income_declared;
        return (
          <div key={k} className="grid grid-cols-[1fr_auto] gap-3 items-center text-sm">
            <span>{a.nombre || `Adulto ${i + 1}`} <span className="text-xs text-muted-foreground">(usado hoy: {formatCLP(base || 0)})</span></span>
            <MoneyInput className="h-9 rounded-lg w-40 text-right" placeholder="Sin cambio" value={items[k] ?? null} disabled={!canAdjust}
              onChange={(v) => setItems({ ...items, [k]: v })} data-testid={`income-adjust-${i}`} />
          </div>
        );
      })}
      {(app.income?.others || []).map((o, j) => {
        const k = otherKey(o, j);
        return (
          <div key={k} className="grid grid-cols-[1fr_auto] gap-3 items-center text-sm">
            <span>Otro ingreso: {o.tipo || "—"}{o.descripcion ? ` · ${o.descripcion}` : ""} <span className="text-xs text-muted-foreground">(declarado: {formatCLP(o.amount || 0)})</span></span>
            <MoneyInput className="h-9 rounded-lg w-40 text-right" placeholder="Sin cambio" value={items[k] ?? null} disabled={!canAdjust}
              onChange={(v) => setItems({ ...items, [k]: v })} data-testid={`income-adjust-other-${j}`} />
          </div>
        );
      })}
      <p className="text-xs text-muted-foreground">Escribe el monto correcto para reemplazar el que usa el sistema (usa 0 para no considerarlo). Deja «Sin cambio» para mantenerlo.</p>
      {extra.map((x, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
          <Input className="h-9 rounded-lg text-sm" placeholder="Ej: Cónyuge (ingreso no declarado)" value={x.label || ""} disabled={!canAdjust}
            onChange={(e) => setExtraAt(i, "label", e.target.value)} data-testid={`income-extra-label-${i}`} />
          <MoneyInput className="h-9 rounded-lg w-40 text-right" value={x.amount ?? null} disabled={!canAdjust}
            onChange={(v) => setExtraAt(i, "amount", v)} data-testid={`income-extra-amount-${i}`} />
          <button type="button" onClick={() => setExtra(extra.filter((_, j) => j !== i))} disabled={!canAdjust} className="text-destructive" data-testid={`income-extra-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {canAdjust && (
        <button type="button" onClick={() => setExtra([...extra, { label: "", amount: null }])} className="text-xs text-primary underline inline-flex items-center gap-1" data-testid="btn-add-income-extra">
          <Plus className="w-3 h-3" /> Agregar ingreso no declarado
        </button>
      )}
      <Input className="h-9 rounded-lg text-sm" placeholder="Nota (por qué se ajusta)" value={note} disabled={!canAdjust}
        onChange={(e) => setNote(e.target.value)} data-testid="income-adjust-note" />
      {review.at && <p className="text-xs text-muted-foreground">Último ajuste: {review.by_name || "—"} · {new Date(review.at).toLocaleString("es-CL")}</p>}
      {canAdjust && (
        <Button onClick={save} disabled={busy} className="btn-lift h-10 rounded-xl px-5" data-testid="btn-adjust-income">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar ingresos y recalcular"}
        </Button>
      )}
    </div>
  );
}
